import { SvgIconComponent } from '@mui/icons-material';
import { ChipProps, TypographyProps } from '@mui/material';
import { SxProps, SystemStyleObject, Theme } from '@mui/system';
import { Icons } from '../icons/Icons';
import { ApprovalStatus, ApprovalStepStatus } from './types';

const APPROVAL_ERRORS = {
    // Save approval errors
    approvalDataInvalidErrorMessage: 'The approval data is invalid and can not be saved yet!',
    saveApprovalMutationErrorMessage: 'The save workflow mutation failed with an error in the response!',
    saveApprovalServerErrorMessage: 'The save workflow operation failed with a server error!',

    // Read workflow errors
    workflowStatusValueInvalid: 'Workflow status value received in query result is invalid: ',
    workflowEntityReferenceMissing: 'Workflow entity reference is missing from the workflow context!',
    workflowStepAlternateIdMissing: 'Workflow step is missing an alternate ID value for step at index: ',
    workflowStepAlternateIdLabelInvalid: 'Workflow step has an invalid alternate ID label for step at index: ',
    workflowStepContactMissing: 'Workflow step contact is missing for step at index: ',
    workflowStepStatusValueInvalid: 'Workflow step status value is invalid for step at index: ',
    workflowStepOccurredAtValueInvalid: 'Workflow step occurredAt value is invalid for step at index: ',
    workflowIsSequentialValueInvalid: 'Workflow steps isSequential values are invalid or inconsistent!',

    // Execute workflow errors
    approvalActionInputInvalidErrorMessage: 'The approval action input is invalid and can not be executed!',
    approvalActionInputStepIdMissingErrorMessage: 'The approval action input is missing the step ID value!',
    approvalActionInputActionMissingErrorMessage: 'The approval action input is missing the action value!',
    executeApprovalMutationErrorMessage: 'The execute workflow step mutation failed with an error in the response!',
    executeApprovalServerErrorMessage: 'The execute workflow step operation failed with a server error!'
} as const;

export class ApprovalDataInvalidError extends Error {
    constructor(message?: string) {
        super(message || APPROVAL_ERRORS.approvalDataInvalidErrorMessage);
        this.name = 'ApprovalDataInvalidError';
    }
}

export class SaveApprovalMutationError extends Error {
    constructor(message?: string) {
        super(message || APPROVAL_ERRORS.saveApprovalMutationErrorMessage);
        this.name = 'SaveApprovalMutationError';
    }
}

export class SaveApprovalServerError extends Error {
    constructor(message?: string) {
        super(message || APPROVAL_ERRORS.saveApprovalServerErrorMessage);
        this.name = 'SaveApprovalServerError';
    }
}

export class WorkflowStatusValueInvalidError extends Error {
    constructor(status?: string, message?: string) {
        super(message || `${APPROVAL_ERRORS.workflowStatusValueInvalid} ${status}`);
        this.name = 'WorkflowStatusValueInvalidError';
    }
}

export class WorkflowEntityReferenceMissingError extends Error {
    constructor(message?: string) {
        super(message || APPROVAL_ERRORS.workflowEntityReferenceMissing);
        this.name = 'WorkflowEntityReferenceMissingError';
    }
}

export class WorkflowStepAlternateIdMissingError extends Error {
    constructor(stepIndex?: number, message?: string) {
        super(message || `${APPROVAL_ERRORS.workflowStepAlternateIdMissing}${stepIndex}`);
        this.name = 'WorkflowStepAlternateIdMissingError';
    }
}

export class WorkflowStepAlternateIdLabelInvalidError extends Error {
    constructor(stepIndex?: number, message?: string) {
        super(message || `${APPROVAL_ERRORS.workflowStepAlternateIdLabelInvalid}${stepIndex}`);
        this.name = 'WorkflowStepAlternateIdLabelInvalidError';
    }
}

export class WorkflowStepContactMissingError extends Error {
    constructor(stepIndex?: number, message?: string) {
        super(message || `${APPROVAL_ERRORS.workflowStepContactMissing}${stepIndex}`);
        this.name = 'WorkflowStepContactMissingError';
    }
}

export class WorkflowStepStatusValueInvalidError extends Error {
    constructor(stepIndex?: number, status?: string, message?: string) {
        super(message || `${APPROVAL_ERRORS.workflowStepStatusValueInvalid}${stepIndex} [${status}]`);
        this.name = 'WorkflowStepStatusValueInvalidError';
    }
}

export class WorkflowStepOccurredAtValueInvalidError extends Error {
    constructor(stepIndex?: number, occurredAt?: string, message?: string) {
        super(message || `${APPROVAL_ERRORS.workflowStepOccurredAtValueInvalid}${stepIndex} [${occurredAt}]`);
        this.name = 'WorkflowStepOccurredAtValueInvalidError';
    }
}

export class WorkflowIsSequentialValueInvalidError extends Error {
    constructor(message?: string) {
        super(message || APPROVAL_ERRORS.workflowIsSequentialValueInvalid);
        this.name = 'WorkflowIsSequentialValueInvalidError';
    }
}

export class ApprovalActionInputInvalidError extends Error {
    constructor(message?: string) {
        super(message || APPROVAL_ERRORS.approvalActionInputInvalidErrorMessage);
        this.name = 'ApprovalActionInputInvalidError';
    }
}

export class ApprovalActionInputStepIdMissingError extends Error {
    constructor(message?: string) {
        super(message || APPROVAL_ERRORS.approvalActionInputStepIdMissingErrorMessage);
        this.name = 'ApprovalActionInputStepIdMissingError';
    }
}

export class ApprovalActionInputActionMissingError extends Error {
    constructor(message?: string) {
        super(message || APPROVAL_ERRORS.approvalActionInputActionMissingErrorMessage);
        this.name = 'ApprovalActionInputActionMissingError';
    }
}

export class ExecuteApprovalMutationError extends Error {
    constructor(message?: string) {
        super(message || APPROVAL_ERRORS.executeApprovalMutationErrorMessage);
        this.name = 'ExecuteApprovalMutationError';
    }
}

export class ExecuteApprovalServerError extends Error {
    constructor(message?: string) {
        super(message || APPROVAL_ERRORS.executeApprovalServerErrorMessage);
        this.name = 'ExecuteApprovalServerError';
    }
}

export const APPROVAL_STEP_ID_NAMESPACE = 'INTERNAL_RECIPIENT_ID';

export const APPROVAL_STRATEGIES = {
    allApprovalsRequired: 'allApprovalsRequired',
    allApprovalsRequiredInSequence: 'allApprovalsRequiredInSequence',
    anyOneApprovalRequired: 'anyOneApprovalRequired'
} as const;

export const APPROVAL_STATUS = {
    draft: 'draft',
    sent: 'sent',
    completed: 'completed',
    declined: 'declined',
    cancelled: 'cancelled',
    initiateFailed: 'initiate failed'
} as const;

export const APPROVAL_STEP_STATUS = {
    pending: 'pending',
    completed: APPROVAL_STATUS.completed,
    declined: APPROVAL_STATUS.declined
} as const;

export const APPROVAL_PROVIDER = {
    docusign: 'DocuSign',
    internal: 'Internal'
} as const;

export const APPROVAL_CONTACT_ROLE = {
    approver: 'Approver',
    signer: 'Signer',
    carbonCopy: 'CarbonCopy'
} as const;

export const APPROVAL_ALLOWED_ACTION = {
    read: 'Read',
    edit: 'Edit',
    delete: 'Delete',
    share: 'Share',
    comment: 'Comment',
    inactivate: 'Inactivate',
    activate: 'Activate',
    purge: 'Purge'
} as const;

export const APPROVAL_EDITABLE_PANEL_HEADER_SX: SxProps<Theme> = {
    mb: 0.5
};

export const APPROVAL_EDITABLE_PANEL_CONTENT_SX: SystemStyleObject<Theme> = {
    mt: 0.5,
    flex: 1
};

export const APPROVAL_STATE_FIELD_NAME_APPROVAL_STRATEGY = 'approvalStrategy' as const;

export const APPROVAL_STATUS_STYLE_MAP: Record<
    ApprovalStatus | ApprovalStepStatus,
    {
        icon: SvgIconComponent;
        chipColor: ChipProps['color'];
        textColor: TypographyProps['color'];
    }
> = {
    [APPROVAL_STATUS.draft]: {
        icon: Icons.Edit,
        chipColor: 'default',
        textColor: 'textSecondary'
    },
    [APPROVAL_STATUS.initiateFailed]: {
        icon: Icons.ApprovalRequestInitiateFailed,
        chipColor: 'error',
        textColor: 'error'
    },
    [APPROVAL_STATUS.sent]: {
        icon: Icons.ApprovalRequestSent,
        chipColor: 'info',
        textColor: 'info'
    },
    [APPROVAL_STATUS.cancelled]: {
        icon: Icons.ApprovalRequestCancelled,
        chipColor: 'default',
        textColor: 'textSecondary'
    },
    [APPROVAL_STATUS.completed]: {
        icon: Icons.ApprovalRequestApproved,
        chipColor: 'success',
        textColor: 'success'
    },
    [APPROVAL_STATUS.declined]: {
        icon: Icons.ApprovalRequestRejected,
        chipColor: 'error',
        textColor: 'error'
    },
    [APPROVAL_STEP_STATUS.pending]: {
        icon: Icons.Pending,
        chipColor: 'warning',
        textColor: 'warning'
    }
} as const;
